import {Task} from '../../app/modules/mytasks/core/_models'
import {EmptySchedulePlaceholder} from '../../app/modules/schedule/components/schedule-view/components/empty-schedule-placeholder/EmptySchedulePlaceholder'
import {formatTaskDateTimeDisplay} from './TaskDateTime'

export const toAbsoluteUrl = (pathname: string) => process.env.PUBLIC_URL + pathname

const apiBaseUrl = () =>
  process.env.REACT_APP_THEME_API_URL || process.env.REACT_APP_API_URL || ''

/**
 * Retorna a URL do avatar: se já for URL absoluta (http/https), usa como está;
 * senão monta como apiBaseUrl + '/storage/' + avatar (path local ou key S3 instances/...;
 * o backend redireciona /storage/instances/* para a URL assinada do S3).
 */
export const getAvatarUrl = (avatar: string | null | undefined): string => {
  if (!avatar || avatar === 'null' || typeof avatar !== 'string' || !avatar.trim()) return ''
  if (avatar.startsWith('http://') || avatar.startsWith('https://')) return avatar
  return apiBaseUrl() + '/storage/' + avatar
}

/**
 * Retorna a URL de um anexo (tarefa ou ticket): se já for URL absoluta (http/https,
 * caso de URL assinada do S3), usa como está; senão monta como apiBaseUrl + '/storage/' + file
 * (path local legado).
 */
export const getAttachmentUrl = (file: string | null | undefined): string => {
  if (!file || file === 'null' || typeof file !== 'string' || !file.trim()) return ''
  if (file.startsWith('http://') || file.startsWith('https://')) return file
  return apiBaseUrl() + '/storage/' + file
}

export const secondsToHours = (timeInSeconds: number) => {
  var timeFormated = '00:00:00'
  if (timeInSeconds !== undefined) {
    var h =
      Math.floor(timeInSeconds / 3600) < 10
        ? `0${Math.floor(timeInSeconds / 3600)}`
        : Math.floor(timeInSeconds / 3600)
    var m = `0${Math.floor((timeInSeconds % 3600) / 60)}`.slice(-2)
    var s = `0${Math.floor((timeInSeconds % 3600) % 60)}`.slice(-2)
    timeFormated = h + ':' + m + ':' + s
  }
  return timeFormated
}

export const secondsToHoursSimplified = (timeInSeconds: number) => {
  if (timeInSeconds === undefined || timeInSeconds === 0) {
    return '0h'
  }
  const hours = Math.floor(timeInSeconds / 3600)
  return hours > 0 ? `${hours}h` : '0h'
}

export const secondsToHoursFormatNotSeconds = (timeInSeconds: number) => {
  const seconds = Number(timeInSeconds)
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00'
  const hours = Math.floor(seconds / 3600)
  const minutes = `0${Math.floor((seconds % 3600) / 60)}`.slice(-2)
  const hoursStr = hours < 10 ? `0${hours}` : String(hours)
  return `${hoursStr}:${minutes}`
}

export const hoursToSeconds = (timeInHours: string) => {
  var hourSplit = timeInHours.split(':')
  if (hourSplit.length === 2) {
    var hourToSeconds = +hourSplit[0] * 60 * 60 + +hourSplit[1] * 60 + +0
  } else {
    var hourToSeconds = +hourSplit[0] * 60 * 60 + +hourSplit[1] * 60 + +hourSplit[2]
  }

  return hourToSeconds
}

export const secondsToDays = (timeInSeconds: number) => {
  return Math.ceil(timeInSeconds / (1000 * 3600 * 24))
}

export const diffInDays = (dateA: string, dateB: string) => {
  let date1 = new Date(dateA)
  let date2 = new Date(dateB)

  let diffInTime = date1.getTime() - date2.getTime()

  let diffInDays = Math.round(diffInTime / (1000 * 3600 * 24))

  return diffInDays
}

export const diffInMonths = (initialDate: string, finalDate: string) => {
  let date1 = new Date(initialDate)
  let date2 = new Date(finalDate)
  return Math.max(
    (date2.getFullYear() - date1.getFullYear()) * 12 + date2.getMonth() - date1.getMonth(),
    0
  )
}

export const diffInYears = (initialDate: string, finalDate: string) => {
  return Math.round(diffInMonths(initialDate, finalDate) / 12)
}

export const diffInFullYears = (initialDate: string, finalDate: string) => {
  let date1 = new Date(initialDate)
  let date2 = new Date(finalDate)

  return Math.max(date2.getFullYear() - date1.getFullYear(), 0)
}

export function createBrazilianDate(date: string) {
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(date)) {
    return new Date(date.replace(' ', 'T'))
  }

  // Se a data está no formato YYYY-MM-DD (ISO)
  if (date.includes('-') && date.length === 10) {
    const [year, month, day] = date.split('-')
    return new Date(`${year}-${month}-${day}T00:00:00`)
  }

  // Se a data está no formato DD/MM/YYYY (brasileiro)
  if (date.includes('/')) {
    const [day, month, year] = date.split('/')
    return new Date(`${year}-${month}-${day}T00:00:00`)
  }

  // Fallback para outros formatos
  return new Date(date)
}

export const formatDateToISO = (brazilianDate: string) => {
  const [day, month, year] = brazilianDate.split('/')
  return `${year}-${month}-${day}`
}

export const formatDateToBrazilian = (date: string) => {
  if (!date) {
    return ''
  }

  const datetimeDisplay = formatTaskDateTimeDisplay(date)
  if (datetimeDisplay) {
    return datetimeDisplay
  }

  // Se a data está no formato YYYY-MM-DD (ISO)
  if (date.includes('-') && date.length === 10) {
    const [year, month, day] = date.split('-')
    return `${day}/${month}/${year}`
  }

  // Se a data já está no formato DD/MM/YYYY (brasileiro)
  if (date.includes('/')) {
    return date
  }

  // Fallback: tentar converter qualquer outro formato
  try {
    const dateObj = new Date(date)
    if (!isNaN(dateObj.getTime())) {
      const day = dateObj.getDate().toString().padStart(2, '0')
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
      const year = dateObj.getFullYear().toString()
      return `${day}/${month}/${year}`
    }
  } catch (e) {
    // Se não conseguir converter, retorna a data original
  }

  return date
}

/**
 * Calcula os dias em atraso de uma tarefa
 * @param expectedDate Data esperada de entrega (YYYY-MM-DD ou DD/MM/YYYY)
 * @param endDate Data de conclusão (opcional, se existir a tarefa já foi entregue)
 * @returns Número de dias em atraso (0 ou negativo se não estiver atrasada)
 */
export const calculateDaysOverdue = (expectedDate: string | undefined, endDate?: string | undefined): number => {
  if (!expectedDate) return 0
  
  // Se a tarefa já foi entregue, não está mais em atraso
  if (endDate) return 0
  
  const expected = createBrazilianDate(expectedDate)
  const now = new Date()

  if (now.getTime() <= expected.getTime()) {
    return 0
  }

  const diffTime = now.getTime() - expected.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  return diffDays > 0 ? diffDays : 0
}

/**
 * Indica se a tarefa está em atraso para exibição no kanban.
 * Em coluna "entregue" (delivered), compara expected_date com end_date;
 * caso contrário, compara expected_date com a data atual.
 * @param expectedDate Data esperada (YYYY-MM-DD ou DD/MM/YYYY)
 * @param options.delivered Se true, a tarefa está na coluna entregue
 * @param options.endDate Data de entrega (usada quando delivered === true)
 * @returns true se a tarefa deve ser exibida como atrasada
 */
export const isTaskOverdue = (
  expectedDate: string | undefined,
  options: {delivered?: boolean; endDate?: string | null}
): boolean => {
  if (!expectedDate) return false
  const {delivered = false, endDate} = options

  if (delivered) {
    if (!endDate) return false
    const expected = createBrazilianDate(expectedDate)
    const endDateStr = String(endDate).slice(0, 10)
    const end = createBrazilianDate(endDateStr)
    return expected.getTime() < end.getTime()
  }

  const expected = createBrazilianDate(expectedDate)
  return new Date().getTime() > expected.getTime()
}

/**
 * Formata a data para exibir "X dias atrás" em português
 * @param date Data a ser formatada
 * @returns String formatada (ex: "6 dias atrás")
 */
export const formatDaysAgo = (date: string | undefined): string => {
  if (!date) return ''
  
  const dateObj = createBrazilianDate(date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const diffTime = today.getTime() - dateObj.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) return ''
  if (diffDays === 1) return '1 dia atrás'
  if (diffDays > 1) return `${diffDays} dias atrás`
  if (diffDays === -1) return ''
  if (diffDays < -1) return `em ${Math.abs(diffDays)} dias`
  
  return ''
}

/**
 * Formata a data para exibir o dia da semana abreviado + data
 * @param date Data a ser formatada
 * @returns String formatada (ex: "ter 05/08/2025")
 */
export const formatDateWithWeekday = (date: string | undefined): string => {
  if (!date) return ''
  
  const dateObj = createBrazilianDate(date)
  const weekdays = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']
  const weekday = weekdays[dateObj.getDay()]
  const formattedDate = formatDateToBrazilian(date)
  
  return `${weekday} ${formattedDate}`
}

export const sortKanbanTasks = (tasks: Task[], column: string) => {
  const lowerCaseColumn = column.toLowerCase()
  if (lowerCaseColumn === 'entregue') {
    return [...tasks].sort((a, b) => {
      const aHasDate = !!a.end_date
      const bHasDate = !!b.end_date

      if (!aHasDate && bHasDate) return -1
      if (aHasDate && !bHasDate) return 1
      if (!aHasDate && !bHasDate) return 0

      // Ambos têm end_date: ordenar por updated_at (ISO string)
      return new Date(b.updated_at!).getTime() - new Date(a.updated_at!).getTime()
    })
  }

  const priorityOrder: Record<string, number> = {
    critica: 1,
    alta: 2,
    media: 3,
    baixa: 4,
  }

  return [...tasks].sort((a, b) => {
    const pA = a.priority ? priorityOrder[a.priority] : 5
    const pB = b.priority ? priorityOrder[b.priority] : 5

    if (pA !== pB) return pA - pB

    const dateA = a.expected_date ? new Date(formatDateToISO(a.expected_date)).getTime() : Infinity
    const dateB = b.expected_date ? new Date(formatDateToISO(b.expected_date)).getTime() : Infinity

    if (dateA !== dateB) return dateA - dateB

    return (
      new Date(formatDateToISO(a.created_at!)).getTime() -
      new Date(formatDateToISO(b.created_at!)).getTime()
    )
  })
}
